import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Reveal } from "@/components/Motion";

const PHASES = [
  ["Education", null, "Learning that a psychological \u201cearthquake\u201d has shattered your core beliefs is normal. This step focuses on understanding physical and emotional trauma responses so individuals can disidentify from the trauma and reduce shame."],
  ["Regulation", null, "Cultivating emotional and physical wellness practices to release anxiety, calm the nervous system, and connect the \u201chead and heart\u201d. This prevents destructive self-medication and promotes rational reflection."],
  ["Disclosure", null, "Moving away from trauma-induced isolation by safely sharing the experience, the personal shortcomings felt, and the grief it caused. This is done through deliberate, purposeful reflection rather than looping intrusive thoughts."],
  ["Story", "Narrative Development", "Rebuilding and writing a completely new chapter of life. The individual builds a positive, forward-looking narrative that integrates the trauma as a part of their history rather than their defining identity."],
  ["Service", null, "Transitioning from victim or survivor to a guide for others. Individuals use their hard-won lessons to enrich their community, families, or teams, giving their past suffering a profound, altruistic purpose."],
];

const muted = { color: "var(--kh-text-muted)" };

const Phase = ({ n, title, sub, text }) => (
  <Reveal className="relative pl-16 sm:pl-24 pb-14 last:pb-0" data-testid={`phase-${n}`}>
    <span className="absolute left-0 top-0 w-11 h-11 sm:w-14 sm:h-14 rounded-full flex items-center justify-center kh-serif text-xl sm:text-2xl" style={{ background: "var(--kh-surface)", border: "1px solid var(--kh-accent)", color: "var(--kh-accent)" }}>
      {n}
    </span>
    <div className="kh-mono" style={{ color: "var(--kh-accent)" }}>Phase {String(n).padStart(2, "0")}</div>
    <h3 className="kh-serif text-3xl sm:text-4xl mt-2">
      {title}
      {sub && <span className="block sm:inline sm:ml-3 text-xl sm:text-2xl" style={muted}>({sub})</span>}
    </h3>
    <p className="mt-4 text-base leading-relaxed max-w-2xl" style={muted}>{text}</p>
  </Reveal>
);

export const Phases = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 60%"] });
  const scaleY = useSpring(scrollYProgress, { stiffness: 90, damping: 24 });
  return (
    <section className="px-6 sm:px-10 lg:px-14 py-24 lg:py-32" style={{ background: "var(--kh-surface)" }} data-testid="phases-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-4">
          <Reveal className="lg:sticky lg:top-32">
            <div className="kh-mono mb-4">The recovery process</div>
            <h2 className="kh-serif text-4xl lg:text-5xl leading-[1.05]">The 5 Phases of the PTG Recovery Process</h2>
          </Reveal>
        </div>
        <div ref={ref} className="lg:col-span-8 relative">
          <div className="absolute left-[21px] sm:left-[27px] top-2 bottom-2 w-px" style={{ background: "var(--kh-border)" }} />
          <motion.div className="absolute left-[21px] sm:left-[27px] top-2 bottom-2 w-px origin-top" style={{ background: "var(--kh-accent)", scaleY }} />
          {PHASES.map(([title, sub, text], i) => <Phase key={title} n={i + 1} title={title} sub={sub} text={text} />)}
        </div>
      </div>
    </section>
  );
};
